import { createContext, ReactNode, useContext } from 'react';
import { useCurrentClientIfAvailable } from 'src/clients/context/CurrentClientProvider';

// A cashflow model is owned by a client (individual model) or a family
// (household model); every scenario hook and REST path is scoped by one.
export type ScenarioScope = { kind: 'client' | 'family'; id: string };

export const scopeBase = (scope: ScenarioScope) =>
  `${scope.kind === 'client' ? 'clients' : 'families'}/${scope.id}`;

// Query keys map straight onto REST paths (key→path convention), so this is
// the single place the two scopes' URLs are derived from.
export const scopeKey = (scope: ScenarioScope) =>
  [scope.kind === 'client' ? 'clients' : 'families', scope.id, 'cashflow-scenarios'] as const;

export const cashflowsPath = (scope: ScenarioScope) => `/${scope.kind}/${scope.id}/cashflows`;

export const modelDetailPath = (scope: ScenarioScope, scenarioId: string) =>
  `${cashflowsPath(scope)}/model/${scenarioId}`;

const ScenarioScopeContext = createContext<ScenarioScope | null>(null);

export function ScenarioScopeProvider({
  scope,
  children,
}: {
  scope: ScenarioScope;
  children: ReactNode;
}) {
  return <ScenarioScopeContext.Provider value={scope}>{children}</ScenarioScopeContext.Provider>;
}

// Defaults to the surrounding client page's scope so client routes need no
// wrapping; family pages provide their scope explicitly via the provider.
export function useScenarioScope(): ScenarioScope | null {
  const provided = useContext(ScenarioScopeContext);
  const { clientId } = useCurrentClientIfAvailable();
  if (provided) {
    return provided;
  }
  return clientId ? { kind: 'client', id: clientId } : null;
}
